import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Pencil, ChevronUp, X } from "lucide-react";
import { canEdit, getStoredUser, getLegacyPassword } from "@/lib/adminAuth";
import { PAGE_SCHEMAS } from "@/lib/cmsSchemas";

/**
 * EditOnSite — discreet, admin-only "edit this page" pill.
 *
 * Mounted globally on every page. Renders NOTHING unless:
 *   - the current pathname maps to a known CMS page, AND
 *   - localStorage carries an admin/editor session (JWT user with
 *     role editor|owner, OR the legacy admin password).
 *
 * Public visitors NEVER see it. Viewers (role=viewer) NEVER see it.
 *
 * On click it expands to show a list of section groups for the
 * current page. Selecting one deep-links to:
 *   /admin?tab=content&page={slug}&group={group}
 *
 * Aesthetic: amber pill bottom-right, soft halo, minimal — sits inside
 * the same temple-control-room language as the admin status bar.
 */

const PATH_TO_PAGE = {
  "/": "homepage",
  "/experiences": "experiences",
  "/community": "community",
  "/corporate": "corporate",
  "/whats-on": "whats-on",
  "/xr-studio": "xr-studio",
  "/festivals": "festivals",
  "/level-up-xr": "level-up",
  "/interactive-playground": "interactive-playground",
  "/ubuntu-retreat": "ubuntu-retreat",
};

export default function EditOnSite() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());
  const [legacy, setLegacy] = useState(() => Boolean(getLegacyPassword()));

  // Listen to login/logout events from other tabs or after refresh.
  useEffect(() => {
    const sync = () => {
      setUser(getStoredUser());
      setLegacy(Boolean(getLegacyPassword()));
    };
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  // Reset expanded state on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const pageSlug = useMemo(() => {
    const path = location.pathname.replace(/\/+$/, "") || "/";
    return PATH_TO_PAGE[path] || null;
  }, [location.pathname]);

  const schema = useMemo(
    () => PAGE_SCHEMAS.find((s) => s.key === pageSlug),
    [pageSlug],
  );

  // Visibility rules — strict.
  if (!pageSlug || !schema) return null;
  if (location.pathname.startsWith("/admin")) return null;
  const isAdminUser = user && canEdit(user);
  if (!isAdminUser && !legacy) return null;

  return (
    <div
      data-testid="edit-on-site"
      className="fixed bottom-4 left-4 sm:bottom-5 sm:left-5 z-[60] print:hidden"
      style={{ pointerEvents: "none" }}
    >
      <div style={{ pointerEvents: "auto" }}>
        {open ? (
          <ExpandedPanel
            page={pageSlug}
            schema={schema}
            onClose={() => setOpen(false)}
          />
        ) : (
          <CollapsedPill
            page={pageSlug}
            schema={schema}
            onOpen={() => setOpen(true)}
          />
        )}
      </div>
    </div>
  );
}

function CollapsedPill({ page, schema, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="edit-on-site-toggle"
      className="group relative inline-flex items-center gap-2 bg-black/85 backdrop-blur-md border border-amber-400/40 hover:border-amber-300 text-amber-300 hover:text-amber-200 px-4 py-2.5 text-[10px] uppercase tracking-[0.3em] font-bold shadow-[0_10px_40px_rgba(0,0,0,0.5)] transition-colors"
      title={`Edit ${schema.label} content`}
    >
      <span className="pointer-events-none absolute inset-0 -z-10 bg-amber-400/[0.05] blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
      <Pencil className="h-3 w-3" />
      Edit page
      <ChevronUp className="h-3 w-3 opacity-70 group-hover:opacity-100" />
    </button>
  );
}

function ExpandedPanel({ page, schema, onClose }) {
  // Group keys that match the editor's <FieldGroup> ids
  const groups = schema.groups || [];
  return (
    <div
      data-testid="edit-on-site-panel"
      className="w-72 sm:w-80 bg-black/95 backdrop-blur-xl border border-amber-400/30 shadow-[0_30px_80px_rgba(0,0,0,0.65)]"
    >
      <div className="relative px-4 py-3 border-b border-amber-400/20 flex items-center justify-between">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-amber-400/[0.04]" />
        <div className="min-w-0">
          <p className="text-[9px] uppercase tracking-[0.35em] text-amber-400/70 font-bold">
            Temple Control
          </p>
          <p className="font-display font-black text-sm text-white truncate">
            Editing · {schema.label}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="edit-on-site-close"
          aria-label="Close"
          className="text-zinc-400 hover:text-amber-400"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <ul className="max-h-[60vh] overflow-y-auto p-2">
        {/* Quick action — open the page in the editor */}
        <li>
          <Link
            to={`/admin?tab=content&page=${page}`}
            data-testid="edit-on-site-open-editor"
            className="block px-3 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold text-amber-300 hover:bg-amber-400/10 transition-colors"
          >
            Open in CMS →
          </Link>
        </li>
        {groups.map((g) => (
          <li key={g.key}>
            <Link
              to={`/admin?tab=content&page=${page}&group=${g.key}`}
              data-testid={`edit-on-site-group-${g.key}`}
              className="flex items-center justify-between gap-3 px-3 py-2.5 text-zinc-300 hover:bg-amber-400/10 hover:text-amber-300 transition-colors"
            >
              <span className="min-w-0">
                <span className="block text-[11px] uppercase tracking-[0.2em] font-bold truncate">
                  {g.title}
                </span>
                <span className="block text-[10px] text-zinc-500 truncate">
                  {g.description}
                </span>
              </span>
              <Pencil className="h-3 w-3 shrink-0 text-zinc-500" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
